import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import { ACCOUNT_DETAIL_FONT_FAMILY, ACCOUNT_DETAIL_FONT_SX } from "../../../../components/AccountDetailPolicy/accountDetailTypography";
import { formatScore, getPair, getStatusColor } from "./compareUtils";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const formatCount = (value) => {
  const num = toNumber(value);
  return num == null ? "—" : String(num);
};

const formatCardDate = (scanDate, { short = false } = {}) => {
  const raw = String(scanDate || "").replace("T", " ").trim();
  const datePart = raw.slice(0, 10);
  const [year, month, day] = datePart.split("-").map(Number);
  if (!year || !month || !day) return "";
  const label = `${MONTHS[month - 1]} ${day}`;
  return short ? label : `${label}, ${year}`;
};

const percentChange = (first, second) => {
  const a = toNumber(first);
  const b = toNumber(second);
  if (a == null || b == null) return 0;
  if (a === 0) return b === 0 ? 0 : 100;
  return ((b - a) / Math.abs(a)) * 100;
};

const formatPctChange = (value) => {
  const num = toNumber(value) || 0;
  const abs = Math.abs(num);
  const formatted = Number.isInteger(abs) ? String(abs) : abs.toFixed(1).replace(/\.0$/, "");
  return `${formatted}%`;
};

const ratioLabel = (part, total) => {
  if (!total) return "0%";
  const pct = (part / total) * 100;
  const formatted = pct.toFixed(1).replace(/\.0$/, "");
  return `${formatted}%`;
};

const CARD_SX = {
  ...ACCOUNT_DETAIL_FONT_SX,
  p: 2.25,
  minWidth: 0,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  backgroundColor: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: "16px",
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
};

const VIZ_SX = {
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  minHeight: { xs: 120, md: 132 },
  mb: 1.5,
};

const TONES = {
  blue: {
    iconBg: "#eff6ff",
    iconColor: "#2563eb",
    footerBg: "#eff6ff",
    footerColor: "#1e40af",
  },
  green: {
    iconBg: "#ecfdf5",
    iconColor: "#059669",
    footerBg: "#ecfdf5",
    footerColor: "#047857",
  },
  purple: {
    iconBg: "#f5f3ff",
    iconColor: "#7c3aed",
    footerBg: "#f5f3ff",
    footerColor: "#6d28d9",
  },
};

const DatabaseMark = () => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    sx={{ width: 20, height: 20, display: "block" }}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <ellipse cx="12" cy="6.2" rx="7" ry="2.6" />
    <path d="M5 6.2v11.2c0 1.45 3.13 2.6 7 2.6s7-1.15 7-2.6V6.2" />
    <path d="M5 12c0 1.45 3.13 2.6 7 2.6s7-1.15 7-2.6" />
  </Box>
);

const ChangeBadge = ({ label, variant = "neutral" }) => {
  const down = variant === "down";
  const up = variant === "up" || variant === "zeroUp";
  return (
    <Box
      sx={{
        px: 1,
        py: 0.35,
        borderRadius: "999px",
        fontSize: "0.6875rem",
        fontWeight: 700,
        fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
        lineHeight: 1.2,
        whiteSpace: "nowrap",
        bgcolor: down ? "#fef2f2" : up ? "#ecfdf5" : "#f8fafc",
        color: down ? "#b91c1c" : up ? "#047857" : "#475569",
        border: `1px solid ${down ? "#fecaca" : up ? "#a7f3d0" : "#e2e8f0"}`,
      }}
    >
      {label}
    </Box>
  );
};

const ValuePair = ({ first, second, firstDate, secondDate }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "1fr auto 1fr",
      alignItems: "baseline",
      columnGap: 1,
      mb: 1.25,
    }}
  >
    <Box sx={{ textAlign: "center" }}>
      <Typography sx={{ fontWeight: 700, fontSize: "1.35rem", color: "#111827", lineHeight: 1.15 }}>
        {first}
      </Typography>
      <Typography sx={{ mt: 0.35, fontSize: "0.8rem", color: "#64748b", fontWeight: 500 }}>
        {firstDate}
      </Typography>
    </Box>
    <Typography sx={{ color: "#cbd5e1", fontWeight: 700, fontSize: "1.1rem", px: 0.5, textAlign: "center" }}>→</Typography>
    <Box sx={{ textAlign: "center" }}>
      <Typography sx={{ fontWeight: 700, fontSize: "1.35rem", color: "#111827", lineHeight: 1.15 }}>
        {second}
      </Typography>
      <Typography sx={{ mt: 0.35, fontSize: "0.8rem", color: "#64748b", fontWeight: 500 }}>
        {secondDate}
      </Typography>
    </Box>
  </Box>
);

const ScoreSparkline = ({ first, second, startLabel, endLabel }) => {
  const a = Math.min(100, Math.max(0, toNumber(first) || 0));
  const b = Math.min(100, Math.max(0, toNumber(second) || 0));
  const w = 320;
  const h = 108;
  const padL = 36;
  const padR = 36;
  const padT = 10;
  const padB = 22;
  const chartW = w - padL - padR;
  const chartH = h - padT - padB;
  const y = (v) => padT + (1 - v / 100) * chartH;
  const x1 = padL;
  const x2 = padL + chartW;
  const y1 = y(a);
  const y2 = y(b);
  const yBase = y(0);
  const y50 = y(50);

  return (
    <Box sx={VIZ_SX}>
      <svg
        viewBox={`0 0 ${w} ${h}`}
        width="100%"
        height="100%"
        preserveAspectRatio="xMidYMid meet"
        style={{ display: "block", maxHeight: 132 }}
        aria-hidden="true"
      >
        <text x="0" y={padT + 4} fill="#64748b" fontSize="10" fontWeight="500" fontFamily={ACCOUNT_DETAIL_FONT_FAMILY}>
          100%
        </text>
        <text x="0" y={y50 + 3} fill="#64748b" fontSize="10" fontWeight="500" fontFamily={ACCOUNT_DETAIL_FONT_FAMILY}>
          50%
        </text>
        <text x="0" y={yBase + 3} fill="#64748b" fontSize="10" fontWeight="500" fontFamily={ACCOUNT_DETAIL_FONT_FAMILY}>
          0%
        </text>
        <line x1={padL} y1={y50} x2={x2} y2={y50} stroke="#e2e8f0" strokeDasharray="3 4" />
        <polygon
          points={`${x1},${yBase} ${x1},${y1} ${x2},${y2} ${x2},${yBase}`}
          fill="#dbeafe"
          opacity="0.85"
        />
        <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#3b82f6" strokeWidth="2.5" />
        <circle cx={x1} cy={y1} r="4.5" fill="#3b82f6" />
        <circle cx={x2} cy={y2} r="4.5" fill="#3b82f6" />
        <text x={x1} y={h - 4} fill="#64748b" fontSize="10" fontWeight="500" fontFamily={ACCOUNT_DETAIL_FONT_FAMILY} textAnchor="start">
          {startLabel}
        </text>
        <text x={x2} y={h - 4} fill="#64748b" fontSize="10" fontWeight="500" fontFamily={ACCOUNT_DETAIL_FONT_FAMILY} textAnchor="end">
          {endLabel}
        </text>
      </svg>
    </Box>
  );
};

const ResourceLegendItem = ({ color, label, count, percent }) => (
  <Box sx={{ textAlign: "center" }}>
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75, mb: 0.15 }}>
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
      <Typography sx={{ fontSize: "0.8125rem", fontWeight: 700, color: "#111827" }}>{label}</Typography>
    </Box>
    <Typography sx={{ fontWeight: 700, fontSize: "0.875rem", color: "#111827", lineHeight: 1.2 }}>
      {formatCount(count)}
    </Typography>
    <Typography sx={{ fontSize: "0.8rem", color: "#64748b", fontWeight: 500 }}>
      {percent}
    </Typography>
  </Box>
);

const ResourceDonut = ({ passed, failed, manual, total }) => {
  const safeTotal = total > 0 ? total : 0;
  const passedPct = safeTotal ? (passed / safeTotal) * 100 : 0;
  const failedPct = safeTotal ? (failed / safeTotal) * 100 : 0;
  const failedEnd = passedPct + failedPct;
  const failedColor = getStatusColor("FAILED");
  const manualColor = getStatusColor("MANUAL");

  return (
    <Box sx={{ ...VIZ_SX, justifyContent: "flex-start", gap: { xs: 1.25, md: 1.5 } }}>
      <Box
        sx={{
          width: 140,
          height: 140,
          flexShrink: 0,
          borderRadius: "50%",
          background: safeTotal
            ? `conic-gradient(#22c55e 0 ${passedPct}%, ${failedColor} ${passedPct}% ${failedEnd}%, ${manualColor} ${failedEnd}% 100%)`
            : "#e2e8f0",
          display: "grid",
          placeItems: "center",
        }}
        aria-hidden="true"
      >
        <Box
          sx={{
            width: 90,
            height: 90,
            borderRadius: "50%",
            bgcolor: "#fff",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: "1.15rem", color: "#111827", lineHeight: 1, textAlign: "center" }}>
            {formatCount(safeTotal)}
          </Typography>
          <Typography sx={{ fontSize: "0.8rem", color: "#64748b", fontWeight: 500, textAlign: "center" }}>Total</Typography>
        </Box>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          columnGap: 0.5,
          rowGap: 0.85,
          minWidth: 0,
          flex: 1,
        }}
      >
        <ResourceLegendItem
          color="#22c55e"
          label="Passed"
          count={passed}
          percent={ratioLabel(passed, safeTotal)}
        />
        <ResourceLegendItem
          color={failedColor}
          label="Failed"
          count={failed}
          percent={ratioLabel(failed, safeTotal)}
        />
        <Box
          sx={{
            gridColumn: "1 / -1",
            display: "flex",
            justifyContent: "center",
            minWidth: 0,
          }}
        >
          <ResourceLegendItem
            color={manualColor}
            label="Manual"
            count={manual}
            percent={ratioLabel(manual, safeTotal)}
          />
        </Box>
      </Box>
    </Box>
  );
};

const PassDots = ({ rate }) => {
  const filled = Math.max(0, Math.min(10, Math.round(((toNumber(rate) || 0) / 100) * 10)));
  return (
    <Box sx={{ display: "flex", gap: 0.75, width: "100%" }} aria-hidden="true">
      {Array.from({ length: 10 }).map((_, index) => (
        <Box
          key={index}
          sx={{
            flex: 1,
            height: 26,
            maxWidth: 32,
            borderRadius: "6px",
            bgcolor: index < filled ? "#7c3aed" : "#e5e7eb",
          }}
        />
      ))}
    </Box>
  );
};

const OverviewCard = ({ tone, icon, title, subtitle, badge, children, footerIcon, footer }) => (
  <Box sx={CARD_SX}>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr auto 1fr",
        alignItems: "flex-start",
        columnGap: 1,
        mb: 1.75,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "10px",
            bgcolor: tone.iconBg,
            color: tone.iconColor,
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
      </Box>
      <Box sx={{ minWidth: 0, textAlign: "center" }}>
        <Typography sx={{ fontWeight: 700, fontSize: "0.875rem", color: "#111827", lineHeight: 1.2, letterSpacing: "0.02em" }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: "0.8rem", color: "#64748b", fontWeight: 500, mt: 0.2 }}>
          {subtitle}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>{badge}</Box>
    </Box>
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
      {children}
    </Box>
    <Box
      sx={{
        flexShrink: 0,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        gap: 1,
        px: 1.25,
        py: 1,
        borderRadius: "10px",
        bgcolor: tone.footerBg,
        color: tone.footerColor,
      }}
    >
      <Box sx={{ mt: "1px", display: "inline-flex" }}>{footerIcon}</Box>
      <Typography sx={{ fontSize: "0.8rem", fontWeight: 500, lineHeight: 1.45, color: tone.footerColor, textAlign: "center" }}>
        {footer}
      </Typography>
    </Box>
  </Box>
);

const CompareSummaryCards = ({ result, baselineDate, compareDate }) => {
  const model = useMemo(() => {
    if (!result) return null;
    const firstSummary = result.firstRun?.summary || {};
    const secondSummary = result.secondRun?.summary || {};
    const summary = result.summary || {};

    const score = getPair(summary.score, firstSummary.score, secondSummary.score);
    const scanned = getPair(
      summary.resourceCounts?.scanned,
      firstSummary.resourceCounts?.scanned,
      secondSummary.resourceCounts?.scanned
    );
    const passedResources = getPair(
      summary.resourceCounts?.passed,
      firstSummary.resourceCounts?.passed,
      secondSummary.resourceCounts?.passed
    );
    const failedResources = getPair(
      summary.resourceCounts?.failed,
      firstSummary.resourceCounts?.failed,
      secondSummary.resourceCounts?.failed
    );
    const manualResources = getPair(
      summary.resourceCounts?.manual ?? summary.resourceCounts?.MANUAL,
      firstSummary.resourceCounts?.manual ?? firstSummary.resourceCounts?.MANUAL,
      secondSummary.resourceCounts?.manual ?? secondSummary.resourceCounts?.MANUAL
    );
    const passedChecks = getPair(
      summary.checkStatusCounts?.PASSED,
      firstSummary.checkStatusCounts?.PASSED,
      secondSummary.checkStatusCounts?.PASSED
    );
    const failedChecks = getPair(
      summary.checkStatusCounts?.FAILED,
      firstSummary.checkStatusCounts?.FAILED,
      secondSummary.checkStatusCounts?.FAILED
    );

    const comparePassedResources = toNumber(passedResources.second) || 0;
    const compareFailedResources = toNumber(failedResources.second) || 0;
    const compareScanned =
      toNumber(scanned.second) || comparePassedResources + compareFailedResources;
    const hasManualKey = [summary.resourceCounts, secondSummary.resourceCounts].some(
      (counts) => counts && (counts.manual != null || counts.MANUAL != null)
    );
    const compareManualResources = hasManualKey
      ? toNumber(manualResources.second) || 0
      : Math.max(0, compareScanned - comparePassedResources - compareFailedResources);
    const comparePassedChecks = toNumber(passedChecks.second) || 0;
    const compareFailedChecks = toNumber(failedChecks.second) || 0;
    const compareTotalChecks = comparePassedChecks + compareFailedChecks;
    const passRate = compareTotalChecks
      ? (comparePassedChecks / compareTotalChecks) * 100
      : 0;

    const scoreDelta = toNumber(score.delta) || 0;
    const scannedDelta = toNumber(scanned.delta) || 0;
    const passedDelta = toNumber(passedChecks.delta) || 0;
    const scannedPct = percentChange(scanned.first, scanned.second);
    const passedPct = percentChange(passedChecks.first, passedChecks.second);

    return {
      score,
      scoreDelta,
      scanned,
      scannedDelta,
      scannedPct,
      comparePassedResources,
      compareFailedResources,
      compareManualResources,
      compareScanned,
      passedChecks,
      passedDelta,
      passedPct,
      comparePassedChecks,
      compareTotalChecks,
      passRate,
    };
  }, [result]);

  if (!model) return null;

  const firstDate = formatCardDate(baselineDate);
  const secondDate = formatCardDate(compareDate);
  const firstShort = formatCardDate(baselineDate, { short: true });
  const secondShort = formatCardDate(compareDate, { short: true });

  const scoreBadge =
    model.scoreDelta === 0
      ? { label: "No Change", variant: "neutral" }
      : {
          label: `${model.scoreDelta > 0 ? "↑" : "↓"} ${formatPctChange(Math.abs(model.scoreDelta))}`,
          variant: model.scoreDelta > 0 ? "up" : "down",
        };

  const scannedBadge = {
    label: `${model.scannedPct >= 0 ? "↑" : "↓"} ${formatPctChange(Math.abs(model.scannedPct))}`,
    variant: model.scannedPct > 0 ? "up" : model.scannedPct < 0 ? "down" : "zeroUp",
  };

  const passedBadge = {
    label: `${model.passedPct >= 0 ? "↑" : "↓"} ${formatPctChange(Math.abs(model.passedPct))}`,
    variant: model.passedPct > 0 ? "up" : model.passedPct < 0 ? "down" : "zeroUp",
  };

  const scoreFooter =
    model.scoreDelta === 0
      ? "Security score remains the same as the previous period."
      : model.scoreDelta > 0
        ? `Security score improved by ${formatPctChange(Math.abs(model.scoreDelta))} compared to the previous period.`
        : `Security score declined by ${formatPctChange(Math.abs(model.scoreDelta))} compared to the previous period.`;

  const scannedFooter =
    model.scannedDelta === 0
      ? "No change in resources scanned compared to the previous period."
      : model.scannedDelta > 0
        ? `Resources scanned increased by ${formatCount(model.scannedDelta)} compared to the previous period.`
        : `Resources scanned decreased by ${formatCount(Math.abs(model.scannedDelta))} compared to the previous period.`;

  const passedFooter =
    model.compareTotalChecks > 0 &&
    model.comparePassedChecks === model.compareTotalChecks &&
    model.passedDelta === 0
      ? "All checks passed. No change from the previous period."
      : model.passedDelta === 0
        ? "Checks passed remains the same as the previous period."
        : model.passedDelta > 0
          ? `Checks passed increased by ${formatCount(model.passedDelta)} compared to the previous period.`
          : `Checks passed decreased by ${formatCount(Math.abs(model.passedDelta))} compared to the previous period.`;

  return (
    <Box
      sx={{
        mb: 2,
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))" },
        gap: 2,
        alignItems: "stretch",
        gridAutoRows: { lg: "1fr" },
      }}
    >
      <OverviewCard
        tone={TONES.blue}
        icon={<ShieldOutlinedIcon sx={{ fontSize: 20 }} />}
        title="Security Score"
        subtitle="Overall security posture"
        badge={<ChangeBadge {...scoreBadge} />}
        footerIcon={<InfoOutlinedIcon sx={{ fontSize: 16 }} />}
        footer={scoreFooter}
      >
        <ValuePair
          first={formatScore(model.score.first)}
          second={formatScore(model.score.second)}
          firstDate={firstDate}
          secondDate={secondDate}
        />
        <ScoreSparkline
          first={model.score.first}
          second={model.score.second}
          startLabel={firstShort}
          endLabel={secondShort}
        />
      </OverviewCard>

      <OverviewCard
        tone={TONES.green}
        icon={<DatabaseMark />}
        title="Resources Scanned"
        subtitle="Total resources analyzed"
        badge={<ChangeBadge {...scannedBadge} />}
        footerIcon={<BarChartOutlinedIcon sx={{ fontSize: 16 }} />}
        footer={scannedFooter}
      >
        <ValuePair
          first={formatCount(model.scanned.first)}
          second={formatCount(model.scanned.second)}
          firstDate={firstDate}
          secondDate={secondDate}
        />
        <ResourceDonut
          passed={model.comparePassedResources}
          failed={model.compareFailedResources}
          manual={model.compareManualResources}
          total={model.compareScanned}
        />
      </OverviewCard>

      <OverviewCard
        tone={TONES.purple}
        icon={<TaskAltIcon sx={{ fontSize: 20 }} />}
        title="Checks Passed"
        subtitle="Security checks completed"
        badge={<ChangeBadge {...passedBadge} />}
        footerIcon={<StarOutlineIcon sx={{ fontSize: 16 }} />}
        footer={passedFooter}
      >
        <ValuePair
          first={formatCount(model.passedChecks.first)}
          second={formatCount(model.passedChecks.second)}
          firstDate={firstDate}
          secondDate={secondDate}
        />
        <Box sx={{ ...VIZ_SX, flexDirection: "column", alignItems: "stretch", gap: 1.5 }}>
          <PassDots rate={model.passRate} />
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "space-between",
              gap: 1,
              width: "100%",
            }}
          >
            <Box sx={{ textAlign: "center", flex: 1 }}>
              <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: "#111827", lineHeight: 1.2 }}>
                {formatCount(model.comparePassedChecks)} / {formatCount(model.compareTotalChecks)}
              </Typography>
              <Typography sx={{ fontSize: "0.8rem", color: "#64748b", fontWeight: 500 }}>
                checks passed
              </Typography>
            </Box>
            <Box sx={{ textAlign: "center", flex: 1 }}>
              <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: "#111827", lineHeight: 1.2 }}>
                {formatScore(model.passRate)}
              </Typography>
              <Typography sx={{ fontSize: "0.8rem", color: "#64748b", fontWeight: 500 }}>
                pass rate
              </Typography>
            </Box>
          </Box>
        </Box>
      </OverviewCard>
    </Box>
  );
};

export default CompareSummaryCards;
